// Der Fluss eines Tages (R19, R25, R27): Sturmuhr, Anlegestellen und ihre Zeichen.
import { encounters, getEncounter, runRules, type EncounterCategory, type EventDef, type RunRules } from '../../content/index.ts';
import type { Rng } from '../rng.ts';
import type { Landing, RunState } from './types.ts';

/** R25: Sturmstufe aus dem Vorsprung */
export function stormStage(lead: number, rules: RunRules = runRules): string {
  for (const s of rules.storm.stages) if (s.above === undefined || lead > s.above + 1e-9) return s.id;
  return rules.storm.stages[rules.storm.stages.length - 1]!.id;
}

/**
 * Auf welche Stufe ein Halt hier den Landtrupp bringt. Q39 (Fabi): Ein Halt dauert so lange wie das, was man dort
 * tut; fight sagt, ob mit einem Kampf gerechnet wird (Anlegen und Kampf), sonst nur das Anlegen.
 */
export function stageIfLanding(run: Pick<RunState, 'lead'>, fight = true, rules: RunRules = runRules): string {
  return stormStage(run.lead - rules.storm.time.land - (fight ? rules.storm.time.fight : 0), rules);
}

function pickWeighted<T extends string>(rng: Rng, weights: Record<T, number>): T {
  const entries = Object.entries(weights) as [T, number][];
  const total = entries.reduce((a, [, w]) => a + w, 0);
  let r = rng.next() * total;
  for (const [k, w] of entries) {
    r -= w;
    if (r < 0) return k;
  }
  return entries[entries.length - 1]![0];
}

/**
 * R6, R7: Zum wahren Inhalt ein Zeichen würfeln, nach den Wahrscheinlichkeiten in run.json. Fabi (Runde 7): Hinter Lärm
 * steckt nur ein Ereignis, das auch nach Lärm klingt (ein Fest, ein Streit, ein Hilferuf).
 */
export function rollSign(rng: Rng, category: EncounterCategory, rules: RunRules = runRules, eventId: string | null = null): string {
  const weights: Record<string, number> = {};
  for (const s of rules.signs) weights[s.id] = s.given[category] ?? 0;
  // R40 (Fabi, Phase Kern, 17.10.2026 nachts): Hinter einem Zeichen steckt nur, was dazu passt; Rauch führt nicht zum hohen Baum
  const fits = eventId !== null ? getEncounter(eventId).signs : undefined;
  if (fits?.length) {
    for (const s of rules.signs) if (!fits.includes(s.group)) weights[s.id] = 0;
    if (Object.values(weights).every((w) => w <= 0)) for (const s of rules.signs) if (fits.includes(s.group)) weights[s.id] = 1;
    return pickWeighted(rng, weights);
  }
  if (category === 'ereignis' && eventId !== null) {
    // Damit Lärm insgesamt so oft ein Ereignis ist wie in run.json, kommen laute Ereignisse entsprechend öfter mit Lärm
    const loud = rules.signs.filter((s) => s.group === 'laerm').reduce((a, s) => a + (s.given.ereignis ?? 0), 0);
    const total = rules.signs.reduce((a, s) => a + (s.given.ereignis ?? 0), 0);
    const share = loud / (total || 1);
    const target = Math.min(0.95, share / noisyShare());
    const factor = getEncounter(eventId).noisy ? (target * (1 - share)) / (share * (1 - target)) : 0;
    for (const s of rules.signs) if (s.group === 'laerm') weights[s.id]! *= factor;
  }
  return pickWeighted(rng, weights);
}

/** Anteil der lauten Ereignisse (Feste, Streit, Hilferufe) an allen Ereignissen, nach Gewicht */
function noisyShare(): number {
  const pool = encounters.filter((e) => e.kind === 'ereignis' && !e.scheduledOnly && !e.pending && e.weight > 0);
  const total = pool.reduce((a, e) => a + e.weight, 0);
  return pool.filter((e) => e.noisy).reduce((a, e) => a + e.weight, 0) / (total || 1) || 1;
}

/** Ob ein Zeichen zur Gruppe Lärm gehört */
export function isLoudSign(signId: string, rules: RunRules = runRules): boolean {
  return rules.signs.find((s) => s.id === signId)?.group === 'laerm';
}

/**
 * P4: Was ein Zeichen meistens bedeutet, aus denselben Zahlen gerechnet, mit denen gewürfelt wird.
 * Ergebnis: Anteil je Begegnungsart, absteigend.
 */
export function signLegend(signId: string, rules: RunRules = runRules): { category: EncounterCategory; share: number }[] {
  const sign = rules.signs.find((s) => s.id === signId)!;
  const total = Object.values(rules.encounterWeights).reduce((a, b) => a + b, 0);
  const raw = rules.categories.map((c) => ({ category: c.id, share: (rules.encounterWeights[c.id] / total) * (sign.given[c.id] ?? 0) }));
  const sum = raw.reduce((a, b) => a + b.share, 0) || 1;
  return raw.map((r) => ({ ...r, share: r.share / sum })).filter((r) => r.share > 0).sort((a, b) => b.share - a.share);
}

/** E43 (Erste Fassung): Ein Ereignis mit Biom kommt nur dort vor; ohne Biom überall */
export const fitsBiome = (e: EventDef, biome: string | undefined): boolean => !e.biomes?.length || (biome !== undefined && e.biomes.includes(biome));

/** Ereignis oder Auftrag für eine Anlegestelle: im Run noch nicht dagewesene bevorzugt (P7), nur was ins Biom passt (E43) */
export function pickEvent(rng: Rng, kind: 'ereignis' | 'auftrag', used: string[], skip: (e: EventDef) => boolean = () => false, biome?: string): string | null {
  const pool = encounters.filter((e) => e.kind === kind && !e.scheduledOnly && !e.pending && e.weight > 0 && !skip(e) && fitsBiome(e, biome));
  if (pool.length === 0) return null;
  const fresh = pool.filter((e) => !used.includes(e.id));
  const list = fresh.length > 0 ? fresh : pool;
  const weights: Record<string, number> = {};
  for (const e of list) weights[e.id] = e.weight;
  return pickWeighted(rng, weights);
}

/**
 * R19, R27: 14 bis 16 Anlegestellen pro Tag (Q44, Fabi). Die meisten sind Kämpfe (E23); jeder Tag hat mindestens
 * einen Händler (BI3: von allem etwas).
 */
export function generateDay(rng: Rng, used: string[], rules: RunRules = runRules, opts: { earlyRecruit?: boolean; day?: number; biome?: string } = {}): Landing[] {
  const cats: EncounterCategory[] = [];
  const { min, max } = rules.landingsPerDay;
  const count = min + Math.floor(rng.next() * (max - min + 1));
  for (let i = 0; i < count; i++) cats.push(pickWeighted(rng, rules.encounterWeights));
  // Spieltest Phase 4 (Fabi): Manches lohnt sich ganz am Anfang noch nicht, etwa der Händler, wenn man kaum Holz hat;
  // und zwei Händler kurz hintereinander fühlen sich blöd an [ZAHL?]
  const day = opts.day ?? 1;
  const earliest = (c: EncounterCategory) => rules.earliest[c]?.[Math.min(day, rules.earliest[c]!.length) - 1] ?? 0;
  let lastTrader = -Infinity;
  for (let i = 0; i < cats.length; i++) {
    if (cats[i] !== 'haendler') continue;
    if (i < earliest('haendler') || i - lastTrader < rules.traderGap) cats[i] = 'kampf';
    else lastTrader = i;
  }
  for (const must of rules.guaranteedPerDay) {
    if (cats.includes(must)) continue;
    // ersetzt einen Kampf an zufälliger Stelle, nicht ganz am Anfang
    const fights = cats.map((c, i) => (c === 'kampf' && i > 0 && i >= earliest(must) ? i : -1)).filter((i) => i >= 0);
    const at = fights.length ? fights[Math.floor(rng.next() * fights.length)]! : 1 + Math.floor(rng.next() * (cats.length - 1));
    cats[at] = must;
  }
  // R27, E28 (Fabi): Damit man früh rotieren kann, bittet am ersten Tag früh ein Tier, mitkommen zu dürfen [ZAHL?].
  // Spieltest Phase 4 (Fabi): auch an späteren Tagen, solange man sonst alle mit an Land nehmen könnte
  let early = -1;
  if (opts.earlyRecruit && rules.earlyRecruitWithin > 0) {
    early = 1 + Math.floor(rng.next() * (rules.earlyRecruitWithin - 1));
    cats[early] = 'ereignis';
  }
  const dayUsed = [...used];
  if (early >= 0) dayUsed.push(rules.earlyRecruitEvent);
  const ids = cats.map((category, i) => {
    let eventId: string | null = null;
    if (i === early) eventId = rules.earlyRecruitEvent;
    else if (category === 'ereignis' || category === 'auftrag') {
      eventId = pickEvent(rng, category, dayUsed, undefined, opts.biome);
      if (eventId) dayUsed.push(eventId);
    }
    return eventId;
  });
  // E43 (Erste Fassung, Runde 4): Jeder Tag hat mindestens so viele Ereignisse aus seinem Biom; sie ersetzen allgemeine
  // Ereignisse, notfalls einen Kampf, nie ganz am Anfang
  const own = (id: string | null) => !!id && !!getEncounter(id).biomes?.length;
  let missing = opts.biome ? rules.biomeEventsPerDay - ids.filter(own).length : 0;
  while (missing > 0) {
    const next = pickEvent(rng, 'ereignis', dayUsed, (e) => !e.biomes?.length, opts.biome);
    if (!next || ids.includes(next)) break;
    const general = ids.map((id, i) => (cats[i] === 'ereignis' && i !== early && i > 0 && id && !own(id) ? i : -1)).filter((i) => i >= 0);
    const fights = cats.map((c, i) => (c === 'kampf' && i > 1 ? i : -1)).filter((i) => i >= 0);
    const from = general.length ? general : fights;
    if (!from.length) break;
    const at = from[Math.floor(rng.next() * from.length)]!;
    cats[at] = 'ereignis';
    ids[at] = next;
    dayUsed.push(next);
    missing -= 1;
  }
  return cats.map((category, i) => {
    const eventId = ids[i] ?? null;
    return { category, eventId, sign: rollSign(rng, category, rules, eventId), seed: Math.floor(rng.next() * 2 ** 31) };
  });
}
